import { ApolloError } from "@apollo/client";
import { Button, Text, TextInput } from "north.js";
import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import styled from "styled-components";

import { FastQrReader, Icon } from "src/shared/components";
import { ViewFinder } from "src/static/img";

import { useMarkHardwareRequestAsPickedUpMutation } from "../../../graphql/markHardwareRequestAsPickedUp.generated";
import { useReturnHackerHardwareRequestMutation } from "../../../graphql/returnHackerHardwareRequest.generated";

interface HardwareQRScannerProps {
  showCameraModal: boolean;
  isCheckout: boolean; // true for checkout, false for return
  setShowCameraModal: (showCameraModal: boolean) => void;
  setShowResultModal: (showResultModal: boolean) => void;
  setScanResult: (result: {
    success: boolean;
    error?: string;
    data?: any;
  }) => void;
}

const ViewFinderIndicator = styled(ViewFinder)`
  top: 0px;
  left: 0px;
  z-index: 100;
  box-sizing: border-box;
  position: absolute;
  width: 100%;
  height: 100%;
  padding: 180px;
  @media screen and (max-width: 1280px) {
    padding: 150px;
  }
  @media screen and (max-width: 768px) {
    padding: 100px;
  }
  margin-top: -50px;
`;

const HardwareQRScanner: React.FC<HardwareQRScannerProps> = ({
  showCameraModal,
  isCheckout,
  setShowCameraModal,
  setShowResultModal,
  setScanResult,
}) => {
  const [requestId, setRequestId] = useState<string>("");
  const [shouldHandleScan, setShouldHandleScan] = useState(false);
  const [manualInput, setManualInput] = useState(false);
  const scanInProgressRef = useRef(false);

  const [markAsPickedUp] = useMarkHardwareRequestAsPickedUpMutation();
  const [returnRequest] = useReturnHackerHardwareRequestMutation();

  const handleScan = async (scannedRequestId: string) => {
    try {
      const id = parseInt(scannedRequestId);
      if (isNaN(id)) {
        setScanResult({
          success: false,
          error: "Invalid request ID format",
        });
        return;
      }

      let response;
      if (isCheckout) {
        response = await markAsPickedUp({
          variables: { id },
        });
      } else {
        response = await returnRequest({
          variables: { id },
        });
      }

      setScanResult({
        success: true,
        data: response.data,
      });
    } catch (e) {
      setScanResult({
        success: false,
        error: (e as ApolloError)?.message || "An error occurred",
      });
    }
  };

  useEffect(() => {
    if (shouldHandleScan && requestId) {
      (async () => {
        await handleScan(requestId);
        setShowResultModal(true);
        setShowCameraModal(false);
        setRequestId("");
        setShouldHandleScan(false);
        scanInProgressRef.current = false;
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldHandleScan, requestId]);

  return (
    <CameraModal
      isOpen={showCameraModal}
      contentLabel="Hardware QR Scanner"
      onRequestClose={() => setShowCameraModal(false)}
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 100,
        },
      }}
    >
      <VideoHeader manualInput={manualInput}>
        <button
          onClick={() => {
            setShowCameraModal(false);
            setManualInput(false);
            setRequestId("");
            scanInProgressRef.current = false;
          }}
        >
          <Icon
            name="chevron-left"
            size="title"
            color={manualInput ? "primary" : "white"}
          />
        </button>
        <div>
          <CheckInInfo>
            <Text
              type="body1"
              style={{
                color: manualInput ? "" : "white",
              }}
            >
              {isCheckout ? "Checkout" : "Return"} Hardware Request
            </Text>
            <Text
              style={{
                color: manualInput ? "" : "white",
              }}
            >
              Scan QR code with request ID
            </Text>
          </CheckInInfo>
        </div>
      </VideoHeader>
      <VideoWrapper>
        {manualInput ? (
          <ManualInputWrapper>
            <Text>Enter the hardware request ID:</Text>
            <BadgeInput
              size="md"
              placeholder="12345"
              value={requestId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setRequestId(e.target.value)
              }
            />
            <ManualInputButtons>
              <Button
                color="secondary"
                size="md"
                onClick={() => setManualInput(false)}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                size="md"
                onClick={() => {
                  if (requestId.trim()) {
                    setShouldHandleScan(true);
                  }
                }}
              >
                Confirm
              </Button>
            </ManualInputButtons>
          </ManualInputWrapper>
        ) : (
          <>
            <FastQrReader
              onResult={async (result) => {
                if (result && !scanInProgressRef.current) {
                  scanInProgressRef.current = true;
                  const scannedText = result.getText();
                  // Extract request ID from QR code (assuming it contains just the ID)
                  setRequestId(scannedText);
                  setShouldHandleScan(true);
                }
              }}
              containerStyle={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              videoContainerStyle={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                padding: "0",
              }}
              videoStyle={{
                width: "100%",
                objectFit: "cover",
                height: "100%",
                position: "relative",
              }}
              ViewFinder={ViewFinderIndicator}
            />
            <ManualButton
              color="primary"
              size="md"
              onClick={() => {
                setManualInput(true);
              }}
            >
              Manually input request ID
            </ManualButton>
          </>
        )}
      </VideoWrapper>
    </CameraModal>
  );
};

const CameraModal = styled(Modal)`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  background: white;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

const VideoHeader = styled.div<{ manualInput: boolean }>`
  width: 100%;
  display: flex;
  background: #1f2937;
  ${({ manualInput }) =>
    manualInput ? `background: white;` : `background: #1f2937;`}
  padding: 29px 25px;
  align-items: center;
  div {
    display: flex;
    flex-direction: column;
    padding-left: 12px;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ManualInputWrapper = styled.div`
  margin-top: 7vh;
  display: flex;
  flex-direction: column;
  padding: 0 30px;
`;

const BadgeInput = styled(TextInput)`
  width: 100%;
  margin-top: 16px;
`;

const ManualInputButtons = styled.div`
  margin-top: 16px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  button {
    width: 100%;
  }
`;

const ManualButton = styled(Button)`
  --buttonWidth: 250px;
  position: fixed;
  width: var(--buttonWidth) !important;
  bottom: 78px;
  left: calc(50vw - var(--buttonWidth) / 2);
  z-index: 10000;
  height: 42px !important;
  * button {
    color: #43afde !important;
  }
`;

const CheckInInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: start;
`;

export default HardwareQRScanner;
